import { useEffect, useState } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { logo } from '@clofo/ui/images'
import { Button } from '@clofo/ui/button'
import { LoadingSpinner } from '@clofo/ui/loading-spinner'
import { usePageTitle } from '@clofo/ui/hooks'
import { useTranslation } from '@clofo/i18n'

import { RequestEmailForm } from '../components/request-email-form'
import { RequestPasswordForm } from '../components/request-password-form'
import { confirmExistingSession, getExistingSession } from '../lib/session'
import { getRedirectTo } from '../lib/redirect'

/**
 * Adaptive login. Step one collects the email and probes for SSO: an SSO-enabled domain redirects
 * to the identity provider, otherwise we advance to the password step. Modeled on the legacy login
 * app's email-first flow.
 *
 * A user who is already signed in skips the form, as in the legacy auth machine's `redirect`
 * action: a live session goes on to the `next` destination (or overview) once the server confirms
 * it, and a password login still waiting on its second factor resumes on its TFA page.
 */
export const LoginPage = () => {
  const { t } = useTranslation()
  usePageTitle(t('login.pageTitle'))

  // When returning from the forgot-password page (its Cancel action) the prior email is carried in
  // navigation state, so we resume directly on the password step instead of the email step.
  const { state, search } = useLocation()
  const [email, setEmail] = useState<string | null>(
    (state as { email?: string } | null)?.email || null,
  )

  const [existingSession] = useState(getExistingSession)
  // The spinner stands in for the form while a live session is confirmed, so it never flashes.
  const [isConfirmingSession, setIsConfirmingSession] = useState(existingSession.status === 'live')

  useEffect(() => {
    if (existingSession.status !== 'live') return
    let ignore = false
    void confirmExistingSession().then(isLive => {
      // A cleaned-up run must not redirect either: once the live run has replaced the URL, `next`
      // is gone and a stale run would send the user on to the default destination instead.
      if (ignore) return
      if (isLive) {
        window.location.replace(getRedirectTo())
      } else {
        setIsConfirmingSession(false)
      }
    })
    return () => {
      ignore = true
    }
  }, [existingSession])

  if (existingSession.status === 'pending-tfa') {
    return <Navigate to={{ pathname: existingSession.path, search }} replace />
  }

  return (
    <main className='flex flex-col bg-background items-center justify-center'>
      <h1 className='sr-only'>{t('login.pageTitle')}</h1>
      <img src={logo} alt='Closing Folders' className='my-13 h-15' />
      {isConfirmingSession ? (
        <LoadingSpinner className='size-8' />
      ) : email === null ? (
        <RequestEmailForm onPasswordRequired={setEmail} />
      ) : (
        <RequestPasswordForm email={email} onChangeEmail={() => setEmail(null)} />
      )}
      {import.meta.env.DEV && (
        // Pinned bottom-right, mirroring the scenario dropdown at bottom-left — dev chrome stays
        // at the screen edges, out of the page flow.
        <div className='fixed bottom-5 right-5 z-50'>
          <Button variant='link' asChild className='p-0'>
            {/* Dev-only stand-in for the invitation email — no email exists against the MSW
                mocks, so this jumps straight to the activation form with a placeholder key. */}
            <Link to='/registration?activationKey=abc123def456'>Open invite link (dev only)</Link>
          </Button>
        </div>
      )}
    </main>
  )
}
